// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { Spinner } from '../../../common/component/Spinner'
import { ContactDetailsType, IContactDto, IContactNestedDto } from '../Contact.model'
import { useContactGetByIdMutation } from '../Contact.api'
import { useAppSelector } from '../../store/Store'
import { selectContact } from '../Contact.slice'

interface Props {
  label: string
  contact: IContactNestedDto
}

export const ContactEmbedered: FC<Props> = ({label, contact}: Props) => {
  const intl = useIntl()
  const storeData = useAppSelector(selectContact)
  const didFetchData = useRef(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<IContactDto | undefined>()
  const [getById] = useContactGetByIdMutation()

  useEffect(() => {
    const fetchData = async (id: string) => {
      try {
        if (!didFetchData.current) {
          setIsLoading(true)
          const response: any = await getById({id})
          if (!response || response.error) {
            setError(intl.formatMessage({id: 'ERROR.MESSAGE.NOT_AVAILABLE'}))
          } else {
            setData(response.data)
          }
        }
      } catch (e: any) {
        setError(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
      } finally {
        setIsLoading(false)
      }
    }
    if (storeData && storeData.id === contact.id) {
      setData(storeData)
      setIsLoading(false)
    } else {
      fetchData(contact.id)
    }
  }, [contact, getById, intl])

  const buildMainLine = (contact: IContactDto): string => {
    let description = ''
    if (contact.email && contact.telephone) {
      description += `${contact.email} | ${contact.telephone}`
    } else if (contact.email) {
      description += `${contact.email}`
    } else if (contact.telephone) {
      description += `${contact.telephone}`
    }

    if (!description && contact.details) {
      const emails = contact.details.filter((detail) => detail.type === ContactDetailsType.EMAIL)
      const telephones = contact.details.filter(
        (detail) => detail.type === ContactDetailsType.TELEPHONE
      )
      if (emails && emails.length > 0 && telephones && telephones.length > 0) {
        const email: string = emails[0].value ?? ''
        const telephone: string = telephones[0].value ?? ''
        description += `${email} | ${telephone}`
      } else if (emails && emails.length > 0) {
        description += `${emails[0].value ?? ''}`
      } else if (telephones && telephones.length > 0) {
        description += `${telephones[0].value ?? ''}`
      }
    }

    if (contact.address && contact.countryCode) {
      return description += description 
        ? ` | ${contact.address} | ${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}` 
        : `${contact.address} | ${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}`
    } else if (contact.address) {
      return description += description 
        ? ` | ${contact.address}` 
        : `${contact.address}`
    } else if (contact.countryCode) {
      return (description += description
        ? ` | ${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}`
        : `${intl.formatMessage({id: `DROPDOWN.COUNTRY.${contact.countryCode}`})}`)
    }
    return description.toUpperCase()
  }

  return (
    <>
      <CustomInputRenderer
        label={label}
        object={
          !data && isLoading ? (
            <Spinner />
          ) : data ? (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-35px symbol-circle mx-2'>
                  <span className='symbol-label bg-secondary text-light-secondary fs-6 fw-bolder'>
                    {data.displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div>
                    <div className='d-flex flex-row justify-content-start align-items-center'>
                      <Link to={`/contacts/${contact.id}`} target='_blank'>
                        <div className='fw-bolder lh-2 fs-7'>
                          <span>{data.displayName}</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                  <div className='text-gray-600 fs-8'>{buildMainLine(data)}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-stack py-2 bg-light-primary'>
              <div className='d-flex flex-stack'>
                <div className='d-flex align-items-center'>
                  <div className='ms-2'>
                    <div className='lh-2 fs-7'>
                      <span className='fw-bolder mb-2'>{contact.displayName}</span>
                    </div>
                    {error && (
                      <span className='text-danger '>
                        <div className='d-flex flex-row justify-content-start align-items-center'>
                          <span className='fs-8 me-1'>
                            <i className='text-danger me-1 bi bi-exclamation-octagon' />
                            {error}
                          </span>
                        </div>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ></CustomInputRenderer>
    </>
  )
}
